import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getExam, createAttempt, submitWriting, pollAttemptUntilMarked } from '../api/exams';
import { SpinnerOverlay } from '../components/index.jsx';
import WritingReportView from '../components/WritingReportView.jsx';
import QuickCreditTopUp from '../components/QuickCreditTopUp.jsx';
import { useAttempt } from '../context/AttemptContext';
import { useAuth } from '../context/AuthContext';
import { getDashboardPlanLabel, createDashboardT } from '../i18n/dashboard';

const theme = {
  pageBg: '#F8FAFC',
  pageBgSoft: '#EEF2FF',
  surface: '#FFFFFF',
  surfaceAlt: '#F8FAFC',
  surfaceSoft: '#F1F5F9',
  border: '#E5E7EB',
  text: '#111827',
  body: '#374151',
  muted: '#9CA3AF',
  accent: '#4F46E5',
  accentSoftBg: '#EEF2FF',
  shadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const splitLines = (text = '') => text.split('\n').map((line) => line.trim()).filter(Boolean);
const countWords = (text = '') => text.trim() ? text.trim().split(/\s+/).length : 0;
const CHECKOUT_RETURN_TO_KEY = 'checkout_return_to';

function UserAvatar({ user, size = 44, textColor = '#374151' }) {
  const [failed, setFailed] = useState(false);
  const picture = user?.picture?.trim();

  if (!picture || failed) {
    return (
      <span style={{ fontWeight: 800, color: textColor }}>
        {user?.name?.[0] || '?'}
      </span>
    );
  }

  return (
    <img
      src={picture}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: 999, objectFit: 'cover' }}
    />
  );
}

export default function Writing() {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const fullExam = searchParams.get('fullExam') === 'true';
  const sharedFet = searchParams.get('sharedFet') === 'true';
  const navigate = useNavigate();
  const { attemptId: sharedAttemptId, setWRes } = useAttempt();
  const { user, logout, refreshUser } = useAuth();

  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(2700);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attemptId, setAttemptId] = useState(sharedAttemptId);
  const [toast, setToast] = useState('');
  const [activeStep, setActiveStep] = useState(1);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [timeExpired, setTimeExpired] = useState(false);
  const [submissionPhase, setSubmissionPhase] = useState('idle');
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const [fontPopoverOpen, setFontPopoverOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [textSize, setTextSize] = useState('normal');
  const [fontStyle, setFontStyle] = useState('default');
  const [lineSpacing, setLineSpacing] = useState('normal');
  const timerRef = useRef(null);
  const navDrawerRef = useRef(null);
  const fontPopoverRef = useRef(null);
  const profileMenuRef = useRef(null);

  const p1 = exam?.questions?.filter((q) => q.part === 1) || [];
  const p2 = exam?.questions?.filter((q) => q.part === 2) || [];
  const part1Question = p1[0] || null;
  const selectedPart2Id = selectedTopic || p2[0]?.id || null;
  const selectedPart2Question = p2.find((q) => q.id === selectedPart2Id) || null;
  const visibleQuestion = activeStep === 1 ? part1Question : (selectedPart2Question || part1Question);
  const totalSteps = p2.length ? 2 : 1;
  const progressPercent = Math.round((activeStep / totalSteps) * 100);
  const isLastStep = activeStep === totalSteps;
  const isMobile = viewportWidth <= 1024;
  const fontSizeMap = { small: 15, normal: 16, large: 18 };
  const fontFamilyMap = {
    default: "'Inter', system-ui, sans-serif",
    serif: "'Georgia', 'Times New Roman', serif",
    dyslexic: "'Arial', sans-serif",
  };
  const lineHeightMap = { compact: 1.65, normal: 1.9, relaxed: 2.15 };
  const backTarget = fullExam || sharedFet ? `/exam/${examId}` : `/hub/${examId}`;
  const backLabel = fullExam ? 'Full Exam' : 'Back';
  const pricingPath = `/pricing?return_to=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
  const t = createDashboardT('ar');
  const writingTextStyle = {
    fontSize: fontSizeMap[textSize] || 16,
    fontFamily: fontFamilyMap[fontStyle] || fontFamilyMap.default,
    lineHeight: lineHeightMap[lineSpacing] || 1.9,
  };
  const timerTone = timeExpired
    ? { label: 'الوقت', status: 'انتهى الوقت', border: '#FCA5A5', badgeBg: '#FEF2F2', badgeText: '#B91C1C', timeColor: '#991B1B', hint: 'تم إغلاق التحرير', shadow: '0 12px 26px rgba(15,23,42,.08)' }
    : timeLeft <= 300
      ? { label: 'الوقت المتبقي', status: 'تنبيه', border: '#FECACA', badgeBg: '#FEF2F2', badgeText: '#B91C1C', timeColor: '#B91C1C', hint: 'أقل من 5 دقائق', shadow: '0 12px 26px rgba(220,38,38,.08)' }
      : timeLeft <= 900
        ? { label: 'الوقت المتبقي', status: 'قريب', border: '#FDE68A', badgeBg: '#FFFBEB', badgeText: '#B45309', timeColor: '#B45309', hint: 'نظّم الوقت جيداً', shadow: '0 12px 26px rgba(217,119,6,.08)' }
        : { label: 'الوقت المتبقي', status: 'نشط', border: '#C7D2FE', badgeBg: '#EEF2FF', badgeText: '#4338CA', timeColor: '#312E81', hint: 'العداد ظاهر دائماً', shadow: '0 12px 26px rgba(79,70,229,.08)' };

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const ex = await getExam(examId);
      if (cancelled) return;
      setExam(ex);
      setTimeLeft((ex.time_mins || 45) * 60);
      setSelectedTopic(ex.questions?.find((q) => q.part === 2)?.id || null);
      if (!sharedAttemptId) {
        setAttemptId(null);
        createAttempt(examId, fullExam ? 'full_exam' : 'practice', 'writing')
          .then((attempt) => {
            if (!cancelled) setAttemptId(attempt.id);
          })
          .catch((error) => {
            console.error(error);
            if (!cancelled) {
              setToast('تم فتح صفحة الكتابة، وجارٍ تجهيز المحاولة. انتظر لحظة ثم أرسل الإجابة.');
            }
          });
      }
    };
    init().catch((error) => {
      console.error(error);
      setToast('Could not prepare writing attempt. Please refresh and try again.');
    });
    return () => { cancelled = true; };
  }, [examId, fullExam, sharedAttemptId]);

  useEffect(() => {
    if (sharedFet) {
      window.sessionStorage.setItem(CHECKOUT_RETURN_TO_KEY, `/exam/${examId}`);
    }
  }, [sharedFet, examId]);

  if (exam && exam.exam_family !== 'fet' && !user?.capabilities?.writing) {
    return <Navigate to={`/pricing?locked=writing&return_to=${encodeURIComponent(`/writing/${examId}${fullExam ? '?fullExam=true' : ''}`)}`} replace />;
  }

  useEffect(() => {
    if (submitting || result || timeLeft <= 0) return undefined;
    timerRef.current = setInterval(() => setTimeLeft((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [submitting, result, timeLeft]);

  useEffect(() => {
    if (timeLeft <= 0) {
      clearInterval(timerRef.current);
      setTimeExpired(true);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!fontPopoverOpen && !navDrawerOpen) return undefined;
    const handleOutside = (event) => {
      if (fontPopoverOpen && fontPopoverRef.current && !fontPopoverRef.current.contains(event.target)) {
        setFontPopoverOpen(false);
      }
      if (navDrawerOpen && navDrawerRef.current && !navDrawerRef.current.contains(event.target)) {
        setNavDrawerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [fontPopoverOpen, navDrawerOpen]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `00:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const clampToWordLimit = (text, limit) => {
    if (!limit || limit < 1) return text;
    const chunks = text.match(/\S+\s*/g);
    if (!chunks || chunks.length <= limit) return text;
    return chunks.slice(0, limit).join('').trimEnd();
  };

  const withTimeout = (promise, ms = 15000) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Writing submission timed out.')), ms)),
  ]);

  const getQuestionLead = (question) => {
    if (!question) return [];
    const instructionLines = splitLines(question.instruction);
    if (instructionLines.length) return instructionLines;
    if (question.question_type === 'email' && question.email_body) return splitLines(question.email_body);
    if (question.prompt_heading) return [question.prompt_heading];
    return question.label ? [question.label] : [];
  };

  const getQuestionBullets = (question) => {
    if (!question) return [];
    if (question.notes?.length) return question.notes;
    if (question.prompt_items?.length) return question.prompt_items;
    return [];
  };

  const getQuestionExtraText = (question) => {
    if (!question) return [];
    if (question.question_type === 'story' && question.story_opener) return [question.story_opener];
    if (question.question_type === 'email' && question.email_subject) return [`Subject: ${question.email_subject}`];
    return [];
  };

  const getWordInstruction = (question, fallback) => {
    if (!question) return fallback;
    if (question.write_instruction) return question.write_instruction;
    if (question.word_count) return `Write around ${question.word_count} words.`;
    return fallback;
  };

  const getTaskHeading = (question, step) => {
    if (!question) return step === 1 ? 'Task 1' : 'Task 2';
    const typeLabel = {
      email: 'Email',
      article: 'Article',
      story: 'Story',
      essay: 'Essay',
      picture: 'Picture prompt',
    }[question.question_type] || 'Writing task';
    return `${step === 1 ? 'Task 1' : 'Task 2'} — ${typeLabel}`;
  };
  const getQuestionTypeLabel = (question) => ({
    email: 'Email',
    article: 'Article',
    story: 'Story',
    essay: 'Essay',
    picture: 'Picture prompt',
  }[question?.question_type] || 'Writing task');

  const jumpToStep = (step) => {
    setActiveStep(step);
    setNavDrawerOpen(false);
  };

  const updateAnswerWithLimit = (question, nextValue) => {
    if (!question?.id || timeExpired || submitting || result) return;
    const limitedValue = clampToWordLimit(nextValue, question.word_count);
    if (limitedValue !== nextValue && question.word_count) {
      setToast(`This answer is limited to ${question.word_count} words.`);
    }
    setAnswers((prev) => ({ ...prev, [question.id]: limitedValue }));
  };

  const buildResponsesForSubmission = () => {
    const responses = [];
    const part1Text = (answers[part1Question?.id] || '').trim();
    const part2Text = (answers[selectedPart2Question?.id] || '').trim();

    if (part1Question && part1Text.length < 5) {
      return { error: 'Question 1 is required before submitting.', responses: [] };
    }
    if (selectedPart2Question && part2Text.length < 5) {
      return { error: 'Please answer one Question 2 option before submitting.', responses: [] };
    }

    if (part1Question && part1Text.length >= 5) {
      responses.push({ question_id: part1Question.id, text: part1Text });
    }
    if (selectedPart2Question && part2Text.length >= 5) {
      responses.push({ question_id: selectedPart2Question.id, text: part2Text });
    }

    return { error: responses.length ? '' : 'Please write at least one answer.', responses };
  };

  const handleSubmit = async () => {
    if (!attemptId) {
      setToast('Writing attempt is still being prepared. Please wait a moment and try again.');
      return;
    }

    const { responses, error } = buildResponsesForSubmission();
    if (error) {
      setToast(error);
      return;
    }

    clearInterval(timerRef.current);
    setSubmitting(true);
    setTimeExpired(true);
    setSubmissionPhase('submitting');

    try {
      await withTimeout(submitWriting(attemptId, responses));
      setSubmissionPhase('generating');
      await refreshUser().catch(() => {});

      const stop = pollAttemptUntilMarked(
        attemptId,
        (attempt) => {
          const writingReport = attempt.writing_report;
          if (writingReport?.mark_status === 'done' || writingReport?.mark_status === 'failed') {
            stop();
            setResult(writingReport);
            setSubmitting(false);
            setSubmissionPhase('idle');
            if (fullExam && writingReport) {
              setWRes({
                total: writingReport.total || 0,
                max_total: writingReport.max_total || 30,
                band: '',
                cefr: '',
                strengths: writingReport.strengths,
                improvements: writingReport.improvements,
                suggestion: writingReport.suggestion,
              });
            }
          }
        },
        () => {
          setSubmitting(false);
          setSubmissionPhase('idle');
          setToast('Writing was submitted, but result updates are taking longer than expected. Please refresh or check results in a moment.');
        },
      );
    } catch (err) {
      console.error(err);
      if (err?.response?.data?.code === 'ai_marking_locked') {
        setToast('Please purchase AI package to get result.');
      } else if (err?.message === 'Writing submission timed out.') {
        setToast('Writing submission is taking too long to start. Please try again or refresh the page.');
      } else {
        setToast(err?.response?.data?.error || 'Could not submit writing right now.');
      }
      setSubmitting(false);
      setSubmissionPhase('idle');
    }
  };

  const handleTryAgain = async () => {
    setAnswers({});
    setResult(null);
    setActiveStep(1);
    setTimeExpired(false);
    setSubmissionPhase('idle');
    if (exam) {
      setTimeLeft((exam.time_mins || 45) * 60);
      setSelectedTopic(exam.questions?.find((q) => q.part === 2)?.id || null);
    }
  };

  if (!exam) return <SpinnerOverlay text="Loading writing test…" />;

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, ${theme.pageBgSoft} 0%, ${theme.pageBg} 100%)`, color: theme.text }}>
      {toast && (
        <div style={{
          position: 'fixed',
          top: 84,
          right: 20,
          zIndex: 50,
          maxWidth: 360,
          background: '#111827',
          color: '#fff',
          padding: '12px 14px',
          borderRadius: 12,
          boxShadow: '0 20px 45px rgba(15,23,42,.28)',
          border: '1px solid rgba(255,255,255,.08)',
          fontSize: 14,
          fontWeight: 600,
        }}>
          {toast}
        </div>
      )}

      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#FFFFFFEE', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${theme.border}` }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <button
              onClick={() => navigate(backTarget)}
              style={{ height: 40, borderRadius: 999, border: `1px solid ${theme.border}`, background: 'transparent', color: theme.body, padding: '0 16px', fontWeight: 700, cursor: 'pointer' }}
            >
              ← {backLabel}
            </button>
            <div style={{ width: 42, height: 42, borderRadius: 14, background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 14, boxShadow: '0 10px 20px rgba(79,70,229,.16)' }}>
              ITC
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}>Writing Test</div>
              <div style={{ fontSize: 12, color: theme.muted, marginTop: 4 }}>{exam.title}{fullExam ? ' · Full exam mode' : ''}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {user && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 999,
                  background: 'linear-gradient(180deg, rgba(255,255,255,.98), rgba(255,247,237,.98))',
                  border: '1px solid rgba(217,119,6,.18)',
                  boxShadow: '0 10px 24px rgba(217,119,6,.10)',
                  color: '#111827',
                  whiteSpace: 'nowrap',
                }}
                title="Available AI credits"
              >
                <strong style={{ fontSize: 16, fontWeight: 900 }}>{user.ai_credits ?? 0}</strong>
                <span style={{ fontSize: 11, color: '#92400E', fontWeight: 800, letterSpacing: '.04em' }}>الرصيد المتبقي</span>
              </div>
            )}
            {user && (
              <QuickCreditTopUp returnTo={`${window.location.pathname}${window.location.search}`} onMessage={setToast} />
            )}
            <div
              style={{
                minWidth: isMobile ? '100%' : 220,
                display: 'grid',
                gap: 8,
                padding: '12px 14px',
                borderRadius: 18,
                background: theme.surface,
                boxShadow: timerTone.shadow,
                border: `1px solid ${timerTone.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: theme.muted, fontWeight: 900, letterSpacing: '.16em', textTransform: 'uppercase' }}>
                  {timerTone.label}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, color: timerTone.badgeText, background: timerTone.badgeBg, padding: '6px 10px', borderRadius: 999 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: timerTone.badgeText }} />
                  {timerTone.status}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: isMobile ? 24 : 28, letterSpacing: '.06em', lineHeight: 1, color: timerTone.timeColor }}>
                  {formatTime(timeLeft)}
                </div>
                <div style={{ fontSize: 12, color: theme.body, fontWeight: 800, textAlign: 'right' }}>
                  وقت الامتحان
                  <div style={{ color: theme.muted, fontSize: 12, marginTop: 4, fontWeight: 700 }}>
                    {timerTone.hint}
                  </div>
                </div>
              </div>
            </div>
            {user && (
              <div ref={profileMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setProfileMenuOpen((open) => !open)}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 999,
                    border: `1px solid ${theme.border}`,
                    background: theme.surface,
                    display: 'grid',
                    placeItems: 'center',
                    boxShadow: theme.shadow,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    padding: 0,
                  }}
                >
                  <UserAvatar user={user} size={42} textColor={theme.body} />
                </button>
                {profileMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    right: 0,
                    width: 280,
                    borderRadius: 20,
                    border: `1px solid ${theme.border}`,
                    background: theme.surface,
                    boxShadow: '0 24px 54px rgba(15,23,42,.18)',
                    overflow: 'hidden',
                    zIndex: 40,
                  }}>
                    <div style={{ padding: 16, borderBottom: `1px solid ${theme.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 999, background: user.picture ? 'transparent' : 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 900, overflow: 'hidden' }}>
                          <UserAvatar user={user} size={44} textColor="#fff" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>{user.name}</div>
                          <div style={{ fontSize: 12, color: theme.muted, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</div>
                        </div>
                      </div>
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: theme.accentSoftBg, color: theme.accent, fontSize: 12, fontWeight: 800 }}>
                          {getDashboardPlanLabel(user.plan, t)}
                        </span>
                        <span style={{ fontSize: 12, color: theme.body, fontWeight: 700 }}>{t('credits', { count: user.ai_credits ?? 0 })}</span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', padding: 10, gap: 6 }}>
                      <button type="button" onClick={() => { navigate('/results'); setProfileMenuOpen(false); }} style={{ height: 42, borderRadius: 12, border: 'none', background: theme.surfaceAlt, color: theme.body, fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '0 14px' }}>
                        Results
                      </button>
                      <button type="button" onClick={() => { navigate(pricingPath); setProfileMenuOpen(false); }} style={{ height: 42, borderRadius: 12, border: 'none', background: theme.surfaceAlt, color: theme.body, fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '0 14px' }}>
                        Billing & Plan
                      </button>
                      <button type="button" onClick={() => { logout(); navigate('/'); }} style={{ height: 42, borderRadius: 12, border: 'none', background: '#FEF2F2', color: '#B91C1C', fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '0 14px' }}>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div ref={fontPopoverRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setFontPopoverOpen((value) => !value)}
                style={{ height: 42, padding: '0 16px', borderRadius: 999, border: `1px solid ${theme.border}`, background: theme.surface, color: theme.body, fontWeight: 700, cursor: 'pointer' }}
              >
                Tools
              </button>
              {fontPopoverOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 260, borderRadius: 18, border: `1px solid ${theme.border}`, background: theme.surface, boxShadow: '0 24px 48px rgba(15,23,42,.12)', padding: 16, zIndex: 30, display: 'grid', gap: 14 }}>
                  <div>
                    <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 800, marginBottom: 8 }}>Quick Actions</div>
                    <button
                      type="button"
                      onClick={() => {
                        setFontPopoverOpen(false);
                        setNavDrawerOpen(true);
                      }}
                      style={{ width: '100%', height: 40, borderRadius: 12, border: `1px solid ${theme.border}`, background: theme.surfaceAlt, color: theme.body, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Open Questions
                    </button>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 800, marginBottom: 8 }}>Text Size</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {['small', 'normal', 'large'].map((value) => (
                        <button key={value} type="button" onClick={() => setTextSize(value)} style={{ flex: 1, minWidth: 70, height: 36, borderRadius: 999, border: `1px solid ${textSize === value ? theme.accent : theme.border}`, background: textSize === value ? theme.accentSoftBg : theme.surfaceAlt, color: textSize === value ? theme.accent : theme.body, fontWeight: 700, cursor: 'pointer' }}>
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 800, marginBottom: 8 }}>Font Style</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {['default', 'serif', 'dyslexic'].map((value) => (
                        <button key={value} type="button" onClick={() => setFontStyle(value)} style={{ flex: 1, minWidth: 72, height: 36, borderRadius: 999, border: `1px solid ${fontStyle === value ? theme.accent : theme.border}`, background: fontStyle === value ? theme.accentSoftBg : theme.surfaceAlt, color: fontStyle === value ? theme.accent : theme.body, fontWeight: 700, cursor: 'pointer' }}>
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 800, marginBottom: 8 }}>Line Spacing</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {['compact', 'normal', 'relaxed'].map((value) => (
                        <button key={value} type="button" onClick={() => setLineSpacing(value)} style={{ flex: 1, minWidth: 72, height: 36, borderRadius: 999, border: `1px solid ${lineSpacing === value ? theme.accent : theme.border}`, background: lineSpacing === value ? theme.accentSoftBg : theme.surfaceAlt, color: lineSpacing === value ? theme.accent : theme.body, fontWeight: 700, cursor: 'pointer' }}>
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 18px 40px' }}>
        {navDrawerOpen && !result && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.35)', zIndex: 35, display: 'flex', justifyContent: 'flex-end' }}>
            <div ref={navDrawerRef} style={{ width: isMobile ? '100%' : 360, maxWidth: '100%', background: theme.surface, borderLeft: `1px solid ${theme.border}`, boxShadow: '-20px 0 40px rgba(15,23,42,.12)', padding: 20, display: 'grid', gap: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 800, marginBottom: 8 }}>Navigation</div>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>Questions</div>
                </div>
                <button type="button" onClick={() => setNavDrawerOpen(false)} style={{ width: 38, height: 38, borderRadius: 999, border: `1px solid ${theme.border}`, background: 'transparent', color: theme.body, cursor: 'pointer' }}>✕</button>
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                <button type="button" onClick={() => jumpToStep(1)} style={{ width: '100%', textAlign: 'left', borderRadius: 16, border: `1px solid ${activeStep === 1 ? theme.accent : theme.border}`, background: activeStep === 1 ? theme.accentSoftBg : theme.surfaceAlt, padding: 16, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: theme.text, marginBottom: 4 }}>Task 1</div>
                      <div style={{ fontSize: 13, color: theme.body }}>{(answers[part1Question?.id] || '').trim() ? 'Answered' : 'Required'}</div>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: (answers[part1Question?.id] || '').trim() ? '#DCFCE7' : '#FEF2F2', color: (answers[part1Question?.id] || '').trim() ? '#166534' : '#B91C1C', fontSize: 12, fontWeight: 800 }}>
                      {(answers[part1Question?.id] || '').trim() ? 'Done' : 'Pending'}
                    </span>
                  </div>
                </button>
                {p2.length > 0 && (
                  <button type="button" onClick={() => jumpToStep(2)} style={{ width: '100%', textAlign: 'left', borderRadius: 16, border: `1px solid ${activeStep === 2 ? theme.accent : theme.border}`, background: activeStep === 2 ? theme.accentSoftBg : theme.surfaceAlt, padding: 16, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: theme.text, marginBottom: 4 }}>Task 2</div>
                        <div style={{ fontSize: 13, color: theme.body }}>{(answers[selectedPart2Question?.id] || '').trim() ? 'Chosen option answered' : 'Choose one option'}</div>
                      </div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: (answers[selectedPart2Question?.id] || '').trim() ? '#DCFCE7' : '#FEF3C7', color: (answers[selectedPart2Question?.id] || '').trim() ? '#166534' : '#92400E', fontSize: 12, fontWeight: 800 }}>
                        {(answers[selectedPart2Question?.id] || '').trim() ? 'Done' : 'Required'}
                      </span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {!result && (
          <div style={{ display: 'grid', gap: 20, gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 300px' }}>
            <div style={{ display: 'grid', gap: 20 }}>
              <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 20, padding: 24, boxShadow: theme.shadow }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 800, marginBottom: 8 }}>
                      Writing Progress
                    </div>
                    <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>
                      {activeStep === 1 ? 'Question 1' : 'Question 2'}
                    </h2>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: theme.accentSoftBg, color: theme.accent, fontSize: 12, fontWeight: 800 }}>
                      Step {activeStep} of {totalSteps}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: theme.surfaceSoft, color: theme.body, fontSize: 12, fontWeight: 800 }}>
                      {Object.values(answers).filter((text) => text.trim().length > 0).length} answers started
                    </span>
                  </div>
                </div>
                <div style={{ height: 8, background: '#E5E7EB', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ width: `${progressPercent}%`, height: '100%', background: 'linear-gradient(90deg, #4F46E5, #6366F1)', borderRadius: 999 }} />
                </div>
              </div>

              {timeExpired && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 16, padding: '16px 18px', color: '#92400E', lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Time completed</div>
                  <div>You can no longer edit your answers. Review your work and submit when ready.</div>
                </div>
              )}

              <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 20, padding: 28, boxShadow: theme.shadow }}>
                {visibleQuestion && (
                  <>
                    <div style={{ marginBottom: 26 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '1.5px', color: theme.muted, fontWeight: 700, marginBottom: 10 }}>
                        Question Section
                      </div>
                      <h2 style={{ fontSize: 28, lineHeight: 1.2, margin: 0, fontWeight: 700, color: theme.text }}>
                        {getTaskHeading(visibleQuestion, activeStep)}
                      </h2>
                    </div>

                    {activeStep === 2 && p2.length > 1 && (
                      <div style={{ display: 'grid', gap: 14, marginBottom: 24 }}>
                        {p2.map((question, index) => {
                          const isSelected = question.id === selectedPart2Id;
                          return (
                            <button
                              key={question.id}
                              type="button"
                              onClick={() => !timeExpired && setSelectedTopic(question.id)}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                borderRadius: 18,
                                border: `1px solid ${isSelected ? theme.accent : theme.border}`,
                                background: isSelected ? theme.accentSoftBg : theme.surfaceAlt,
                                padding: 18,
                                cursor: timeExpired ? 'default' : 'pointer',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
                                    {String.fromCharCode(65 + index)}: {question.prompt_title || question.label || 'Writing option'}
                                  </div>
                                  <div style={{ fontSize: 13, color: theme.body, lineHeight: 1.65 }}>
                                    {getQuestionLead(question)[0] || 'Choose this option'}
                                  </div>
                                </div>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, border: `2px solid ${isSelected ? theme.accent : '#CBD5E1'}`, background: isSelected ? theme.accent : 'transparent' }} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {activeStep === 2 && selectedPart2Question && (
                      <div
                        style={{
                          display: 'grid',
                          gap: 10,
                          marginBottom: 20,
                          padding: '18px 18px',
                          borderRadius: 20,
                          border: `2px solid ${theme.accent}`,
                          borderLeft: `6px solid ${theme.accent}`,
                          background: 'linear-gradient(135deg, rgba(79,70,229,.16), rgba(99,102,241,.10))',
                          boxShadow: '0 16px 34px rgba(79,70,229,.14)',
                        }}
                      >
                        <div style={{ fontSize: 12, color: theme.accent, textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 900 }}>
                          السؤال المحدد
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ display: 'grid', gap: 6 }}>
                            <div style={{ fontSize: 14, fontWeight: 800, color: theme.body }}>
                              أنت الآن تجيب عن هذا الخيار في السؤال الثاني
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 900, color: theme.text }}>
                              {selectedPart2Question.prompt_title || selectedPart2Question.label || 'خيار الكتابة'}
                            </div>
                          </div>
                          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: theme.surface, color: theme.accent, border: `1px solid ${theme.border}`, fontSize: 13, fontWeight: 900 }}>
                            {getQuestionTypeLabel(selectedPart2Question) === 'Article' ? 'مقال' : getQuestionTypeLabel(selectedPart2Question) === 'Story' ? 'قصة' : getQuestionTypeLabel(selectedPart2Question)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
                      {getQuestionLead(visibleQuestion).map((line, index) => (
                        <p key={`${visibleQuestion.id}-lead-${index}`} style={{ color: theme.body, margin: 0, lineHeight: 1.8 }}>
                          {line}
                        </p>
                      ))}
                      {getQuestionExtraText(visibleQuestion).map((line, index) => (
                        <div key={`${visibleQuestion.id}-extra-${index}`} style={{ color: theme.text, fontWeight: 600 }}>
                          {line}
                        </div>
                      ))}
                    </div>

                    {getQuestionBullets(visibleQuestion).length > 0 && (
                      <div style={{ marginBottom: 22 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: theme.body, marginBottom: 12 }}>
                          {visibleQuestion.question_type === 'email' ? 'In the email' : 'Write about'}
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, color: theme.body, lineHeight: 1.9 }}>
                          {getQuestionBullets(visibleQuestion).map((item, index) => <li key={`${visibleQuestion.id}-bullet-${index}`}>{item}</li>)}
                        </ul>
                      </div>
                    )}

                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderRadius: 14, background: theme.accentSoftBg, color: theme.accent, fontWeight: 700, marginBottom: 18 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: theme.accent }} />
                      {getWordInstruction(visibleQuestion, `Write around ${visibleQuestion.word_count || 100} words.`)}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.12em', color: theme.muted, fontWeight: 700 }}>
                        Response
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: theme.surfaceSoft, color: theme.body, fontSize: 12, fontWeight: 700 }}>
                        {countWords(answers[visibleQuestion.id] || '')} words
                      </span>
                    </div>

                    <div style={{ border: `1px solid ${theme.border}`, borderRadius: 18, background: theme.surfaceAlt, padding: 18 }}>
                      <textarea
                        value={answers[visibleQuestion.id] || ''}
                        onChange={(e) => updateAnswerWithLimit(visibleQuestion, e.target.value)}
                        disabled={timeExpired || submitting || Boolean(result)}
                        placeholder="Write your answer here..."
                        style={{
                          width: '100%',
                          minHeight: activeStep === 1 ? 230 : 260,
                          padding: 0,
                          border: 'none',
                          resize: 'vertical',
                          background: 'transparent',
                          color: theme.text,
                          ...writingTextStyle,
                          outline: 'none',
                          opacity: timeExpired ? 0.75 : 1,
                          cursor: timeExpired ? 'not-allowed' : 'text',
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => activeStep === 1 ? navigate(backTarget) : setActiveStep(1)}
                        style={{ height: 46, padding: '0 22px', borderRadius: 999, border: `1px solid ${theme.border}`, background: theme.surface, color: theme.body, fontWeight: 700, cursor: 'pointer' }}
                      >
                        {activeStep === 1 ? 'Back' : 'Previous'}
                      </button>

                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {!isLastStep ? (
                          <button
                            type="button"
                            onClick={() => setActiveStep(2)}
                            style={{ height: 46, padding: '0 24px', borderRadius: 999, border: 'none', background: theme.accent, color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                          >
                            Next &gt;
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || !attemptId}
                            style={{ height: 46, padding: '0 24px', borderRadius: 999, border: 'none', background: theme.accent, color: '#fff', fontWeight: 800, cursor: submitting || !attemptId ? 'not-allowed' : 'pointer', opacity: submitting || !attemptId ? 0.7 : 1 }}
                          >
                            {submitting ? 'Generating Report…' : !attemptId ? 'Preparing…' : fullExam ? 'Submit & Continue' : 'Submit Writing'}
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <aside style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 20, padding: 20, boxShadow: theme.shadow, alignSelf: 'start', display: 'grid', gap: 18 }}>
              <div>
                <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 800, marginBottom: 8 }}>
                  Writing Overview
                </div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>Your progress</div>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16 }}>
                  <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 800, marginBottom: 8 }}>Task 1 · /10</div>
                  <div style={{ fontSize: 14, color: theme.body }}>{(answers[part1Question?.id] || '').trim() ? 'Answered' : 'Not answered yet'}</div>
                </div>
                {p2.length > 0 && (
                  <div style={{ background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 16 }}>
                    <div style={{ fontSize: 11, color: theme.muted, textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 800, marginBottom: 8 }}>Task 2 · /20</div>
                    <div style={{ fontSize: 14, color: theme.body }}>
                      {(answers[selectedPart2Question?.id] || '').trim() ? 'Chosen option answered' : 'Choose one option and answer it'}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ background: '#1E1B4B', borderRadius: 20, padding: 20, color: '#fff' }}>
                <div style={{ fontSize: 11, color: '#A5B4FC', textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 800, marginBottom: 10 }}>
                  Rubric-based marking
                </div>
                <div style={{ fontSize: 16, lineHeight: 1.7, color: '#E0E7FF' }}>
                  Submit both tasks to receive rubric-based AI marking with score breakdown, strengths, improvements, and next-step advice.
                </div>
              </div>
            </aside>
          </div>
        )}

        {submitting && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', zIndex: 45, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ width: 'min(460px, 100%)', borderRadius: 28, background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)', border: `1px solid ${theme.border}`, boxShadow: '0 10px 30px rgba(2,6,23,.20)', padding: '34px 32px', textAlign: 'center' }}>
              <div style={{ width: 72, height: 72, margin: '0 auto 18px', borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(79,70,229,.10)' }}>
                <div style={{ width: 34, height: 34, borderRadius: 999, border: '3px solid rgba(99,102,241,.22)', borderTopColor: theme.accent, animation: 'spin 0.9s linear infinite' }} />
              </div>
              <div style={{ fontSize: 11, color: theme.accent, textTransform: 'uppercase', letterSpacing: '.18em', fontWeight: 800, marginBottom: 10 }}>
                AI Marking
              </div>
              <h3 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: theme.text, letterSpacing: '-0.02em' }}>
                {submissionPhase === 'submitting' ? 'Submitting your answers' : 'Result Report is generating'}
              </h3>
              <p style={{ margin: '12px auto 0', maxWidth: 320, fontSize: 15, lineHeight: 1.75, color: theme.body }}>
                {submissionPhase === 'submitting'
                  ? 'Please wait while we securely send your answers for marking.'
                  : 'Please stay on this page while we prepare your writing report.'}
              </p>
            </div>
          </div>
        )}

        {result && (
          <div style={{ display: 'grid', gap: 26, maxWidth: 920, margin: '0 auto' }}>
            <WritingReportView
              report={result}
              responses={[]}
              theme={theme}
              isMobile={isMobile}
              isTablet={viewportWidth < 1024}
              footer={fullExam ? (
                <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 20, padding: 22, textAlign: 'center', boxShadow: theme.shadow }}>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Writing Complete</div>
                  <div style={{ fontSize: 14, color: theme.body, marginBottom: 18 }}>
                    You can continue to the speaking section when you are ready.
                  </div>
                  <button
                    onClick={() => navigate(`/speaking/${examId}?fullExam=true`)}
                    style={{ height: 48, padding: '0 24px', borderRadius: 999, border: 'none', background: '#0F766E', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                  >
                    Continue to Speaking →
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate(`/hub/${examId}`)}
                    style={{ flex: 1, minWidth: 180, height: 46, borderRadius: 999, border: `1px solid ${theme.border}`, background: theme.surface, color: theme.body, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ← Back to Hub
                  </button>
                  <button
                    onClick={handleTryAgain}
                    style={{ flex: 1, minWidth: 180, height: 46, borderRadius: 999, border: 'none', background: theme.accent, color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                  >
                    Try Again
                  </button>
                </div>
              )}
            />
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
